// CRM 2.0 · M12 Template Builder: the setting of one blank (DC-72): data from the system, leave blank
// (line / space) or fixed text. One pop-up style: Cancel and Save.
import React, { useMemo, useState } from "react";
import cls from "./Templates.module.css";
import { BLANK_KINDS, SUGGESTION_SOURCE, APPLICANT_PICKS } from "./templateCommon";

const OPTIONS = [
  { key: "DATA", title: "Data from the system", text: "Filled from the booking for every customer" },
  { key: "LEAVE_BLANK", title: "Leave blank in the print", text: "Printed as a line or an empty space, filled by hand" },
  { key: "FIXED_TEXT", title: "Fixed text", text: "The same text for every customer" },
];

// inBlock: the blank is in the buyer block (an applicant field repeats there); outside it asks "Which applicant?" (DC-204)
function BlankSettingModal({ blank, fields, settings, onCancel, onSave, inBlock = false, isApplicantField = () => false }) {
  const allowed = settings?.blankAllowedSettings || ["DATA", "LEAVE_BLANK", "FIXED_TEXT"];
  const startSetting = blank.setting || (blank.suggestionSource === "PEN" ? "LEAVE_BLANK" : blank.suggestedFieldKey ? "DATA" : "");
  const [setting, setSetting] = useState(allowed.includes(startSetting) ? startSetting : "");
  const [fieldKey, setFieldKey] = useState(blank.fieldKey || blank.suggestedFieldKey || "");
  const [leaveAs, setLeaveAs] = useState(blank.leaveAs || settings?.leaveBlankStyle || "LINE");
  const [fixedText, setFixedText] = useState(blank.fixedText || "");
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(blank.applicantPick && blank.applicantPick !== "EACH" ? blank.applicantPick : "APPLICANT_1");
  const field = fields.find((f) => f.key === fieldKey);
  const askPick = setting === "DATA" && isApplicantField(field) && !inBlock;

  const groups = useMemo(() => {
    const query = q.trim().toLowerCase();
    const byGroup = {};
    fields
      .filter((f) => !query || f.label.toLowerCase().includes(query) || f.key.toLowerCase().includes(query))
      .forEach((f) => { (byGroup[f.fieldGroup] = byGroup[f.fieldGroup] || []).push(f); });
    return byGroup;
  }, [fields, q]);

  const ready = setting === "DATA" ? !!fieldKey : setting === "LEAVE_BLANK" ? !!leaveAs : setting === "FIXED_TEXT" ? !!fixedText.trim() : false;

  function save() {
    if (setting === "DATA") onSave({ blankNo: blank.blankNo, setting, fieldKey, applicantPick: askPick ? pick : undefined });
    else if (setting === "LEAVE_BLANK") onSave({ blankNo: blank.blankNo, setting, leaveAs });
    else onSave({ blankNo: blank.blankNo, setting, fixedText: fixedText.trim() });
  }

  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label={`Blank ${blank.blankNo}`}>
      <div className={cls.modal}>
        <div className={cls.modalHead}>
          <h2>Blank {blank.blankNo}</h2>
          <p className={cls.hint}>
            {BLANK_KINDS[blank.kind] || blank.kind} · …{blank.anchorBefore} <b className={cls.mono}>{String(blank.rawText || "____").slice(0, 40)}</b> {blank.anchorAfter}…
          </p>
          {blank.suggestionSource && blank.suggestionSource !== "NONE" && (
            <p className={cls.hint} style={{ marginTop: ".6rem" }}>
              Suggested: <b>{blank.suggestedFieldKey || "Leave blank"}</b> ({SUGGESTION_SOURCE[blank.suggestionSource]})
            </p>
          )}
        </div>
        <div className={cls.modalBody}>
          {OPTIONS.filter((o) => allowed.includes(o.key)).map((o) => (
            <label key={o.key} className={`${cls.radio} ${setting === o.key ? cls.radioOn : ""}`}>
              <input type="radio" name="blankSetting" checked={setting === o.key} onChange={() => setSetting(o.key)} />
              <span><b>{o.title}</b><br /><span className={cls.hint}>{o.text}</span></span>
            </label>
          ))}

          {setting === "DATA" && (
            <div>
              <label className={cls.lbl} htmlFor="fieldSearch">Field</label>
              <input id="fieldSearch" className={cls.inp} placeholder="Search fields" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: ".8rem" }} />
              <select className={cls.sel} size={8} value={fieldKey} onChange={(e) => setFieldKey(e.target.value)} aria-label="Field">
                {Object.entries(groups).map(([g, list]) => (
                  <optgroup key={g} label={g}>
                    {list.map((f) => <option key={f.key} value={f.key}>{f.label} · {f.key}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
          {askPick && (
            <div>
              <label className={cls.lbl} htmlFor="applicantPick">Which applicant?</label>
              <select id="applicantPick" className={cls.sel} value={pick} onChange={(e) => setPick(e.target.value)}>
                {APPLICANT_PICKS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
              <p className={cls.hint}>Outside the buyer block. 2nd–4th print empty when the booking has fewer applicants.</p>
            </div>
          )}
          {setting === "DATA" && isApplicantField(field) && inBlock && <p className={cls.hint}>Inside the buyer block: repeats for each applicant.</p>}
          {setting === "LEAVE_BLANK" && (
            <div>
              <label className={cls.lbl} htmlFor="leaveAs">Prints as</label>
              <select id="leaveAs" className={cls.sel} value={leaveAs} onChange={(e) => setLeaveAs(e.target.value)}>
                <option value="LINE">A line ______</option>
                <option value="SPACE">An empty space</option>
              </select>
            </div>
          )}
          {setting === "FIXED_TEXT" && (
            <div>
              <label className={cls.lbl} htmlFor="fixedText">Text</label>
              <input id="fixedText" className={cls.inp} value={fixedText} onChange={(e) => setFixedText(e.target.value)} placeholder="e.g. PUNE" />
            </div>
          )}
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} disabled={!ready} onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}

export default BlankSettingModal;
